import {
  type Filter,
  type FilterId,
  FilterType,
  type FreeTextFilter,
  type GroupFilter,
  type ListFilter,
  type MultiFilter,
  type RangeFilter,
  type SchoolFilter,
  type SegmentedFilter,
  type SingleValueFilter,
  type StaticListFilter,
  type TreeFilter,
} from '../types/filters';
import {
  freeTextFilterHandler,
  groupFilterHandler,
  listFilterHandler,
  schoolFilterHandler,
  singleValueFilterHandler,
  staticListFilterHandler,
  treeFilterHandler,
} from './default-handlers';
import type { FilterDefinition, FilterHandler } from './types';

export interface FilterMultiSpec<
  MultiId extends FilterId,
  Segmented extends boolean,
> {
  readonly id: MultiId;
  readonly segmented?: Segmented;
}

export interface FilterDescriptor<
  F extends Filter = Filter,
  Id extends FilterId = FilterId,
  MultiId extends FilterId = FilterId,
  Segmented extends boolean = boolean,
> {
  readonly id: Id;
  readonly definition: FilterDefinition<F>;
  readonly handler: FilterHandler<F>;
  readonly multi?: FilterMultiSpec<MultiId, Segmented>;
}

interface DescriptorSpec<
  F extends Filter,
  Id extends FilterId,
  MultiId extends FilterId,
  Segmented extends boolean,
> {
  definition: F & { id: Id };
  multi?: FilterMultiSpec<MultiId, Segmented>;
}

interface DefaultedDescriptorSpec<
  F extends Filter,
  Id extends FilterId,
  MultiId extends FilterId,
> extends DescriptorSpec<F, Id, MultiId, false> {
  handler?: Partial<FilterHandler<F>>;
}

const toDescriptor = <
  F extends Filter,
  Id extends FilterId,
  MultiId extends FilterId,
  Segmented extends boolean,
>(
  spec: DescriptorSpec<F, Id, MultiId, Segmented>,
  handler: FilterHandler<F>
): FilterDescriptor<F, Id, MultiId, Segmented> => ({
  id: spec.definition.id,
  definition: spec.definition,
  handler,
  ...(spec.multi === undefined ? {} : { multi: spec.multi }),
});

export const defineListFilter = <
  const Id extends FilterId,
  const MultiId extends FilterId = never,
>(
  spec: DefaultedDescriptorSpec<ListFilter, Id, MultiId>
) => toDescriptor(spec, { ...listFilterHandler, ...spec.handler });

export const defineTreeFilter = <
  const Id extends FilterId,
  const MultiId extends FilterId = never,
>(
  spec: DefaultedDescriptorSpec<TreeFilter, Id, MultiId>
) => toDescriptor(spec, { ...treeFilterHandler, ...spec.handler });

export const defineStaticListFilter = <
  const Id extends FilterId,
  const MultiId extends FilterId = never,
>(
  spec: DefaultedDescriptorSpec<StaticListFilter, Id, MultiId>
) => toDescriptor(spec, { ...staticListFilterHandler, ...spec.handler });

export const defineFreeTextFilter = <
  const Id extends FilterId,
  const MultiId extends FilterId = never,
>(
  spec: DefaultedDescriptorSpec<FreeTextFilter, Id, MultiId>
) => toDescriptor(spec, { ...freeTextFilterHandler, ...spec.handler });

export const defineSchoolFilter = <
  const Id extends FilterId,
  const MultiId extends FilterId = never,
>(
  spec: DefaultedDescriptorSpec<SchoolFilter, Id, MultiId>
) => toDescriptor(spec, { ...schoolFilterHandler, ...spec.handler });

export const defineGroupFilter = <const Id extends FilterId>(
  spec: DefaultedDescriptorSpec<GroupFilter, Id, never>
) => toDescriptor(spec, { ...groupFilterHandler, ...spec.handler });

export const defineSingleValueFilter =
  <T>() =>
  <const Id extends FilterId>(
    spec: DefaultedDescriptorSpec<SingleValueFilter<T>, Id, never>
  ) =>
    toDescriptor(spec, {
      ...singleValueFilterHandler<T>(),
      ...spec.handler,
    });

export const defineFilter = <
  F extends Filter,
  const Id extends FilterId,
  const MultiId extends FilterId = never,
  const Segmented extends boolean = false,
>(
  spec: DescriptorSpec<F, Id, MultiId, Segmented> & {
    handler: FilterHandler<F>;
  }
) => toDescriptor(spec, spec.handler);

type MultiFilterState = Extract<Filter, { type: FilterType.MULTI }>;

interface MultiEntry {
  id: FilterId;
  definition: MultiFilterState;
  handler: FilterHandler<MultiFilterState>;
}

export const wrapAsMulti = (
  descriptor: FilterDescriptor,
  multi: FilterMultiSpec<FilterId, boolean>
): MultiEntry => {
  const { definition: wrappedDefinition, handler: wrapped } = descriptor;
  const definition: MultiFilterState = {
    id: multi.id,
    name: wrappedDefinition.name,
    type: FilterType.MULTI,
    wrappedFilterId: descriptor.id,
    instances: [],
    isVisible: true,
  };

  if (multi.segmented === true) {
    return {
      id: multi.id,
      definition,
      handler: {
        isActive: (state) => state.instances.length > 0,
        isDefault: (state) => state.instances.length === 0,
        getEffectiveFilter: (state) => state,
      },
    };
  }

  return {
    id: multi.id,
    definition,
    handler: {
      isActive(state) {
        const instances: Filter[] = state.instances;
        return instances.some((instance) => wrapped.isActive(instance));
      },
      isDefault: (state) => state.instances.length === 0,
      getEffectiveFilter(state, _definition, config) {
        const instances: Filter[] = state.instances;
        return {
          ...state,
          instances: instances.map((instance) =>
            wrapped.getEffectiveFilter(instance, wrappedDefinition, config)
          ),
        } as MultiFilterState;
      },
    },
  };
};

type MultiFilterOf<D> =
  D extends FilterDescriptor<infer F, FilterId, FilterId, infer Segmented>
    ? true extends Segmented
      ? F extends RangeFilter
        ? MultiFilter<SegmentedFilter<F>>
        : never
      : F extends
            | FreeTextFilter
            | TreeFilter
            | RangeFilter
            | ListFilter
            | StaticListFilter
            | SchoolFilter
        ? MultiFilter<F>
        : never
    : never;

type MultiIdOf<D> = D extends { multi?: FilterMultiSpec<infer M, boolean> }
  ? M
  : never;

type FilterOf<D> = D extends FilterDescriptor<infer F> ? F : never;

export type DefinitionMapOf<D extends FilterDescriptor> = {
  [Desc in D as Desc['id']]: FilterDefinition<FilterOf<Desc>>;
} & {
  [Desc in D as MultiIdOf<Desc>]: FilterDefinition<MultiFilterOf<Desc>>;
};
